import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames';

import {
  Button,
  CenteredContainer,
  Link,
  Select,
  Title,
  Type,
} from '@nerdwallet/currency';
import classes from '@nerdwallet/base-styles/classes';
import Image from '@nerdwallet/react-image';
import {
  ElementImpression,
  formSubmit,
} from '@nerdwallet/nw-client-lib/tracking';
import { Impression as BedrockImpression } from '@nerdwallet/analytics/react/impression';
import {
  setTrk,
  TrkElement,
  TrkTag,
} from '@nerdwallet/nw-client-lib/trk-helper';

import { links } from '~/client/lib/front-page-content-src/front-page-internal-links';

const CCTool = ({
  preheader,
  header,
  subtitle,
  subtitleMobile,
  buttonText,
  backgroundImage,
  backgroundClassname,
}) => {
  const [cardCategory, setCardCategory] = useState('cash-rewards');
  const [creditScore, setCreditScore] = useState('excellent');

  const handleCreditScoreChange = (event) => {
    const newCreditScore = event?.target?.value;
    formSubmit({
      formName: 'front-page',
      fieldName: 'credit-score',
      fieldValue: newCreditScore,
    });
    setCreditScore(newCreditScore);
  };

  const handleCardCategoryChange = (event) => {
    const newCardCategory = event?.target?.value;
    formSubmit({
      formName: 'front-page',
      fieldName: 'card-category',
      fieldValue: newCardCategory,
    });
    setCardCategory(newCardCategory);
  };

  const toolPageMap = {
    rewards: 'rewardscards',
    'cash-rewards': 'cashbackcards',
    'travel-miles': 'travelcards',
    'low-interest': 'lowinterestcards',
    'zero-percent': 'zeropercentcards',
    'balance-transfer': 'balancetransfercards',
    secured: 'securedcards',
  };

  const buttonDestination = [
    '/',
    cardCategory,
    '-credit-cards?credit_score=',
    creditScore,
    `&trk=hp_findcardstool_${toolPageMap[cardCategory]}`,
    '&trk_source=hero-cta',
    '&trk_origin=front-page',
    `&trk_copy=${toolPageMap[cardCategory]}`,
  ].join('');

  const registrationUrl = setTrk({
    url: links.registration,
    params: {
      trk: 'hp_getcreditscore_registration',
      [TrkTag.element]: TrkElement.filter,
      [TrkTag.location]: 'score-dropdown',
      [TrkTag.copy]: 'dont_know_your_score_get_it_free',
    },
  });

  const imageProps = {
    src: backgroundImage,
    alt: '',
    backgroundColor: 'transparent',
    role: 'presentation',
    naturalWidth: 319,
    naturalHeight: 319,
  };

  return (
    <BedrockImpression
      eventType="form_impression"
      entityName="find-cards-tool"
      payload={{ form_name: 'credit_card_driver_priority_tool' }}
    >
      <ElementImpression
        sectionCategory="find-cards-tool"
        sectionPosition={2}
        id="find-cards-tool"
        elementName="find-cards-tool"
        elementCategory="image"
        elementPosition={0}
        elementDomType="div"
        parentElementDomType="div"
        parentElementDomId="cc-tool"
      >
        <CenteredContainer className="cc-tool-cont">
          <Image
            {...imageProps}
            className={classNames(['cc-tool-img', `${backgroundClassname}`])}
          />
          <div className={classNames(['cc-tool-wrapper', 'cc-tool'])}>
            <div className="cc-tool__text">
              {preheader && (
                <Type bold uppercase size={1} className="cc-tool__preheader">
                  {preheader}
                </Type>
              )}
              <Title size="large" className={classes('margin-vertical-3')}>
                {header}
              </Title>
              <Image
                {...imageProps}
                className={`${backgroundClassname}__mobile`}
              />
              <Type className="cc-tool__subtitle">{subtitle}</Type>
              <Type className="cc-tool__subtitle-mobile">{subtitleMobile}</Type>
            </div>
            <form className="tool-form" autoComplete="off">
              <div className="tool-container">
                <div className="tool-container__select">
                  <Select
                    className="tool-container__inner-select"
                    label="My credit score is"
                    value={creditScore}
                    options={[
                      {
                        value: 'excellent',
                        label: 'Excellent (720 - 850)',
                      },
                      {
                        value: 'good',
                        label: 'Good (690 - 719)',
                      },
                      {
                        value: 'average',
                        label: 'Average (630 - 689)',
                      },
                      {
                        value: 'poor',
                        label: 'Poor (350 - 629)',
                      },
                    ]}
                    onChange={handleCreditScoreChange}
                    fullWidth
                  />
                </div>
                <div className="tool-container__select">
                  <Select
                    className="tool-container__inner-select"
                    label="I care most about"
                    value={cardCategory}
                    options={[
                      {
                        value: 'cash-rewards',
                        label: 'Cash back',
                      },
                      {
                        value: 'secured',
                        label: 'Building my credit',
                      },
                      {
                        value: 'rewards',
                        label: 'Rewards',
                      },
                      {
                        value: 'travel-miles',
                        label: 'Travel',
                      },
                      {
                        value: 'balance-transfer',
                        label: 'Balance transfer',
                      },
                      {
                        value: 'low-interest',
                        label: 'Low interest',
                      },
                      {
                        value: 'zero-percent',
                        label: 'Zero percent',
                      },
                    ]}
                    onChange={handleCardCategoryChange}
                    fullWidth
                  />
                </div>
                <div className="tool-container__btn">
                  <Button
                    className="cc-tool-btn"
                    href={buttonDestination}
                    primary
                  >
                    {buttonText}
                  </Button>
                </div>
              </div>
              <Link href={registrationUrl}>
                Don&apos;t know your score? Get it free.
              </Link>
            </form>
          </div>
        </CenteredContainer>
      </ElementImpression>
    </BedrockImpression>
  );
};

CCTool.propTypes = {
  preheader: PropTypes.string,
  header: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  buttonText: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundClassname: PropTypes.string,
};

CCTool.displayName = 'CCTool';

export default CCTool;
